import { OnObject3dInit, OnObject3dPointerEnter, OnObject3dPointerExit, Visualize3dUserData } from '@magicore/visualize-3d';
import {
  Color,
  DoubleSide,
  EventDispatcher,
  HSL,
  LineSegments,
  Material,
  MeshBasicMaterial,
  MeshPhongMaterial,
  Object3D,
  Texture
} from 'three';
import { restoreOriginalMaterial, setMaterial, Visualize3dComponent, Visualize3dObject } from '@magicore/visualize-3d';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import type { ThreeJsMeshLike } from '@magicore/visualize-3d';

interface ActiconInteractiveComponent extends Visualize3dComponent {
  entityId: string;
  containerEntityId: string;
  hoverColor: string;
  selectedColor: string;
  warningColor: string;
  errorColor: string;
}

interface ActiconInteractiveComponentUserData {
  hoverMaterial: Material;
  selectMaterial: Material;
  warningMaterial: Material;
  errorMaterial: Material;
}

const enum MaterialType {
  Hover,
  Selected,
  Warning,
  Error
}

/**
 * Handler for managing interactive 3D objects with Acticon-specific logic
 */
@Injectable({
  providedIn: 'root'
})
export class ActiconInteractiveComponentHandler
  implements
    OnObject3dInit<ActiconInteractiveComponent>,
    OnObject3dPointerEnter<ActiconInteractiveComponent>,
    OnObject3dPointerExit<ActiconInteractiveComponent>
{
  private selectedEntityIds$ = new BehaviorSubject<string[]>([]);
  private overlappingEntityIds$ = new BehaviorSubject<string[]>([]);
  private errorEntityIds$ = new BehaviorSubject<string[]>([]);

  private hoverMaterials: Record<string, Material> = {};
  private selectMaterials: Record<string, Material> = {};
  private warningMaterials: Record<string, Material> = {};
  private errorMaterials: Record<string, Material> = {};

  private dragAndDropMode: boolean;
  private lineEditor: boolean;

  initialize(object3d: Object3D, _: Visualize3dObject, component: ActiconInteractiveComponent): void {
    const entityId = component.entityId;
    const containerEntityId = component.containerEntityId;

    const objectsWithMaterials = (object3d.userData as Visualize3dUserData).objectsWithMaterials;

    // Generate hover- and selection materials needed for this object
    for (const materialObject of objectsWithMaterials) {
      if (this.shouldAdjustMaterial(materialObject)) {
        this.createInteractionMaterialsForObject(materialObject, component);
      }
    }

    const entityStateSubscription = combineLatest([this.selectedEntityIds$, this.overlappingEntityIds$, this.errorEntityIds$]).subscribe(
      ([selectedEntityIds, overlappingEntityIds, errorEntityIds]) => {
        const selected = selectedEntityIds.includes(containerEntityId);
        const overlapping = overlappingEntityIds.includes(containerEntityId);
        const error = errorEntityIds.includes(entityId);
        if (error && selected) {
          this.setMaterial(object3d, this.lineEditor ? MaterialType.Error : MaterialType.Selected);
        } else if (error) {
          this.setMaterial(object3d, MaterialType.Error);
        } else if (selected) {
          this.setMaterial(object3d, MaterialType.Selected);
        } else if (overlapping) {
          this.setMaterial(object3d, MaterialType.Warning);
        } else {
          this.restoreMaterial(object3d);
        }
      }
    );

    const firstEventDispatcherMesh = objectsWithMaterials.find(c => c instanceof EventDispatcher) as EventDispatcher;

    const onDispose = () => {
      entityStateSubscription.unsubscribe();
      // @ts-expect-error: avoid ts error
      firstEventDispatcherMesh.removeEventListener('dispose', onDispose);
    };
    // @ts-expect-error: avoid ts error
    firstEventDispatcherMesh?.addEventListener('dispose', onDispose);
  }

  onPointerEnter(object3d: Object3D, _: Visualize3dObject, __: ActiconInteractiveComponent): void {
    if (this.dragAndDropMode) return;
    this.setMaterial(object3d, MaterialType.Hover);
  }

  onPointerExit(object3d: Object3D, _: Visualize3dObject, component: ActiconInteractiveComponent): void {
    if (this.dragAndDropMode) return;

    const selected = this.selectedEntityIds$.value.includes(component.containerEntityId);
    const overlapping = this.overlappingEntityIds$.value.includes(component.containerEntityId);
    const error = this.errorEntityIds$.value.includes(component.entityId);

    if (error && selected) {
      this.setMaterial(object3d, this.lineEditor ? MaterialType.Error : MaterialType.Selected);
    } else if (error) {
      this.setMaterial(object3d, MaterialType.Error);
    } else if (selected) {
      this.setMaterial(object3d, MaterialType.Selected);
    } else if (overlapping) {
      this.setMaterial(object3d, MaterialType.Warning);
    } else {
      this.restoreMaterial(object3d);
    }
  }

  /**
   * Generates hover- and selection materials needed for the given object and caches them in the Material map. If fitting
   * materials already exist, the cached ones will be used.
   * @param object The object to create materials for
   * @param component The V3D interactive component
   */
  private createInteractionMaterialsForObject(object: ThreeJsMeshLike, component: ActiconInteractiveComponent): void {
    /*
     * We use the base material color for the object to define what the hover/selection colors will be.
     * We do this by lerping (a linear interpolation between two values)
     * the original color towards the target color based on the lightness of the original color.
     * This way dark colors stay dark and light colors get adjusted greatly.
     */

    const generateMaterialColorFromOriginalColor = (originalColor: Color, targetColor: Color): Color => {
      const result = new Color(originalColor);
      const hsl: HSL = { h: 0, s: 0, l: 0 };
      originalColor.getHSL(hsl);
      return result.lerp(targetColor, hsl.l);
    };

    const baseMaterialColor = this.getMaterialColorFromObject(object);

    const hoverMaterialColor = generateMaterialColorFromOriginalColor(baseMaterialColor, new Color(component.hoverColor));
    const selectMaterialColor = generateMaterialColorFromOriginalColor(baseMaterialColor, new Color(component.selectedColor));
    const warningMaterialColor = generateMaterialColorFromOriginalColor(baseMaterialColor, new Color(component.warningColor));
    const errorMaterialColor = generateMaterialColorFromOriginalColor(baseMaterialColor, new Color(component.errorColor));

    if (!this.hoverMaterials[hoverMaterialColor.getHexString()]) {
      this.hoverMaterials[hoverMaterialColor.getHexString()] = this.createMaterial(hoverMaterialColor, 0.65);
    }
    if (!this.selectMaterials[selectMaterialColor.getHexString()]) {
      this.selectMaterials[selectMaterialColor.getHexString()] = this.createMaterial(selectMaterialColor, 0.65);
    }
    if (!this.warningMaterials[warningMaterialColor.getHexString()]) {
      this.warningMaterials[warningMaterialColor.getHexString()] = this.createMaterial(warningMaterialColor, 0.6);
    }
    if (!this.errorMaterials[errorMaterialColor.getHexString()]) {
      this.errorMaterials[errorMaterialColor.getHexString()] = this.createMaterial(errorMaterialColor, 0.9);
    }

    // Set materials to object userData for easy access
    (object.userData as ActiconInteractiveComponentUserData).hoverMaterial = this.getHoverMaterialForColor(hoverMaterialColor);
    (object.userData as ActiconInteractiveComponentUserData).selectMaterial = this.getSelectMaterialForColor(selectMaterialColor);
    (object.userData as ActiconInteractiveComponentUserData).warningMaterial = this.getWarningMaterialForColor(warningMaterialColor);
    (object.userData as ActiconInteractiveComponentUserData).errorMaterial = this.getErrorMaterialForColor(errorMaterialColor);
  }

  /**
   * Set the selected entity ids. This allows an object to be selected from outside Visualize3D
   * @param entityId
   */
  public setSelectedEntityIds(entityId: string | string[]): void {
    this.selectedEntityIds$.next(Array.isArray(entityId) ? entityId : [entityId]);
  }

  public forceRefresh(): void {
    this.selectedEntityIds$.next(this.selectedEntityIds$.value);
  }

  public addToSelectedEntityIds(entityId: string | string[]): void {
    const ids = Array.isArray(entityId) ? entityId : [entityId];
    this.selectedEntityIds$.next([...this.selectedEntityIds$.value, ...ids]);
  }

  public removeFromSelectedEntityIds(entityId: string | string[]): void {
    const ids = Array.isArray(entityId) ? entityId : [entityId];
    this.selectedEntityIds$.next(this.selectedEntityIds$.value.filter(i => !ids.includes(i)));
  }

  public toggleSelectedId(entityId: string) {
    if (this.selectedEntityIds$.value.includes(entityId)) {
      this.removeFromSelectedEntityIds(entityId);
    } else {
      this.addToSelectedEntityIds(entityId);
    }
  }

  public setDragAndDropMode(value: boolean) {
    this.dragAndDropMode = value;
  }

  public setLineEditorMode(value: boolean) {
    this.lineEditor = value;
  }

  public getSelectedIds() {
    return this.selectedEntityIds$.value;
  }

  public highLightOverlap(entityIds: string[]) {
    this.overlappingEntityIds$.next([...this.overlappingEntityIds$.value, ...entityIds]);
  }

  public unHighLightOverlap(entityIds: string[]) {
    this.overlappingEntityIds$.next(this.overlappingEntityIds$.value.filter(i => !entityIds.includes(i)));
  }

  public resetOverlap() {
    this.overlappingEntityIds$.next([]);
  }

  public setOverlappingIds(entityIds: string[]) {
    this.overlappingEntityIds$.next(entityIds);
  }

  public setErrorEntityIds(entityIds: string[]) {
    this.errorEntityIds$.next(entityIds);
  }

  private getHoverMaterialForColor(color: Color) {
    const key = color.getHexString();
    return this.hoverMaterials[key];
  }

  private getSelectMaterialForColor(color: Color) {
    const key = color.getHexString();
    return this.selectMaterials[key];
  }

  private getWarningMaterialForColor(color: Color) {
    const key = color.getHexString();
    return this.warningMaterials[key];
  }

  private getErrorMaterialForColor(color: Color) {
    const key = color.getHexString();
    return this.errorMaterials[key];
  }

  private createMaterial(color: Color, opacity: number): MeshBasicMaterial {
    return new MeshBasicMaterial({
      side: DoubleSide,
      color: color,
      transparent: true,
      opacity
    });
  }

  private restoreMaterial(object3d: Object3D): void {
    for (const object of (object3d.userData as Visualize3dUserData).objectsWithMaterials) {
      if (this.shouldAdjustMaterial(object)) {
        restoreOriginalMaterial(object);
      }
    }
  }

  private setMaterial(object3d: Object3D, materialType: MaterialType): void {
    for (const object of (object3d.userData as Visualize3dUserData).objectsWithMaterials) {
      if (this.shouldAdjustMaterial(object)) {
        const userData = object.userData as ActiconInteractiveComponentUserData;

        let material: Material;
        if (materialType === MaterialType.Selected) {
          material = userData.selectMaterial;
        } else if (materialType === MaterialType.Error) {
          material = userData.errorMaterial;
        } else if (materialType === MaterialType.Warning) {
          material = userData.warningMaterial;
        } else {
          material = userData.hoverMaterial;
        }

        if (object.material !== material) {
          setMaterial(object, material, false);
        }
      }
    }
  }

  private shouldAdjustMaterial(object: ThreeJsMeshLike): boolean {
    // Don't adjust the material of line segments (e.g. dimension lines)
    const isLineSegments = object instanceof LineSegments;
    // Don't adjust the material of text geometries. The "textgeometry"-name is set by Visualize3D
    const isText = object.geometry?.name.startsWith('textgeometry');
    // Don't override materials with textures
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    const objectHasTexture = materials.some(mat => mat.map instanceof Texture);

    return !isLineSegments && !isText && !objectHasTexture;
  }

  private getMaterialColorFromObject(object: ThreeJsMeshLike): Color {
    const color = new Color();
    const material = Array.isArray(object.material) ? object.material[0] : object.material;

    if (material instanceof MeshBasicMaterial || material instanceof MeshPhongMaterial) {
      color.copy(material.color);
    }

    return color;
  }
}
